import { type Locale } from '@syncanix/shared';
import { cn } from '@syncanix/ui';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import { useT } from '@/core/I18nProvider';
import { localizedPath } from '@/i18n/locale-path';

import { DOC_SECTIONS, getSectionForSlug } from './content/docs-nav';
import { DocsSearch } from './DocsSearch';

import type { ReactElement } from 'react';

/**
 * The docs sidebar: instant search on top, then one collapsible group per
 * section (native `<details>` — SSR-safe, keyboard-accessible for free). The
 * reader's current section is expanded and the rest stay folded, so all
 * fourteen sections fit on screen instead of one endless list. The active
 * section remounts on every in-section navigation (the `key` carries the slug)
 * so it re-opens even if the reader folded it, and the active link is scrolled
 * into view within the rail.
 */
export function DocsSidebar({
  locale,
  currentSlug,
  withSlashShortcut = false,
  onNavigate,
}: {
  readonly locale: Locale;
  readonly currentSlug?: string | undefined;
  readonly withSlashShortcut?: boolean;
  readonly onNavigate?: () => void;
}): ReactElement {
  const { t } = useT();
  const activeLinkRef = useRef<HTMLAnchorElement>(null);
  const activeSectionId =
    currentSlug !== undefined ? getSectionForSlug(currentSlug)?.id : DOC_SECTIONS[0]?.id;

  useEffect(() => {
    const link = activeLinkRef.current;
    if (link !== null && typeof link.scrollIntoView === 'function') {
      link.scrollIntoView({ block: 'nearest' });
    }
  }, [currentSlug]);

  return (
    <div className="flex flex-col gap-4">
      <DocsSearch locale={locale} withSlashShortcut={withSlashShortcut} />
      <nav aria-label={t('docs.nav.sidebarLabel')} className="flex flex-col gap-1">
        {DOC_SECTIONS.map((section) => {
          const isActiveSection = section.id === activeSectionId;
          return (
            <details
              // Remount the active section per slug so it re-opens on navigation.
              key={isActiveSection ? `${section.id}:${currentSlug ?? ''}` : section.id}
              open={isActiveSection}
              className="group"
            >
              <summary
                className={cn(
                  'flex cursor-pointer select-none items-center justify-between gap-2 rounded-md px-2 py-1.5 font-mono rtl:font-sans text-[10.5px] uppercase tracking-[0.13em] text-[color:var(--bd-faint)] rtl:normal-case rtl:tracking-normal transition-colors hover:bg-[color:var(--bd-sunk)] hover:text-[color:var(--bd-ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--bd-blue)] [&::-webkit-details-marker]:hidden',
                  isActiveSection ? 'text-[color:var(--bd-ink)]' : 'text-[color:var(--bd-muted)]',
                )}
              >
                {t(section.titleKey)}
                <svg
                  aria-hidden="true"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="h-3.5 w-3.5 shrink-0 text-[color:var(--bd-muted)] transition-transform group-open:rotate-180"
                >
                  <path
                    d="m4 6 4 4 4-4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </summary>
              {/* One "you are here" device across the whole docs shell: a blue
                  rule replacing the rail's hairline. The sidebar, the on-this-page
                  TOC and the index directory all use it, so a reader learns it
                  once. `-ms-px` pulls each row back over the rail so its own
                  border sits ON the line rather than beside it. */}
              <ul className="mb-2 ms-2 mt-1 flex flex-col border-s border-[color:var(--bd-line)]">
                {section.pages.map((page) => {
                  const active = page.slug === currentSlug;
                  return (
                    <li key={page.slug} className="-ms-px">
                      <Link
                        ref={active ? activeLinkRef : undefined}
                        to={localizedPath(`/docs/${page.slug}`, locale)}
                        aria-current={active ? 'page' : undefined}
                        onClick={onNavigate}
                        className={cn(
                          'block border-s-2 py-1.5 pe-2 ps-3 text-[0.86rem] leading-[1.4] transition-colors duration-200',
                          'focus-visible:rounded-e-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--bd-blue)]',
                          active
                            ? 'border-[color:var(--bd-blue)] bg-[color:var(--bd-tint)] font-medium text-[color:var(--bd-ink)]'
                            : 'border-transparent text-[color:var(--bd-muted)] hover:border-[color:var(--bd-dot)] hover:text-[color:var(--bd-ink)]',
                        )}
                      >
                        {t(page.titleKey)}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </details>
          );
        })}
      </nav>
    </div>
  );
}
